import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Compass, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * TrafficSourcesCard — admin-only breakdown of page_views by source
 * channel (Google, Facebook, Email, Direct, etc.) over a chosen window.
 *
 * Built to answer the recurring "GSC says I have 5 clicks but my
 * dashboard shows 200 visitors — where are they coming from?" question.
 *
 * Visual: horizontal bar list ordered by page_views desc, each row
 * shows source name + share % + bar + raw count. Hover/expand reveals
 * the top 3 raw referrers for that bucket so you can see exactly which
 * Facebook post or external blog drove the traffic.
 */
const WINDOWS = [
  { value: 1, label: "Today" },
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];

// Soft color hints — picked so the most common buckets stand out without
// requiring a 50-color palette. Everything else falls back to neutral.
const SOURCE_TINTS = {
  "Google Search": "bg-blue-500",
  "Bing Search": "bg-blue-400",
  "DuckDuckGo Search": "bg-yellow-500",
  "Yahoo Search": "bg-purple-500",
  Facebook: "bg-blue-700",
  Instagram: "bg-pink-500",
  TikTok: "bg-gray-900 dark:bg-gray-100",
  "X / Twitter": "bg-gray-700",
  LinkedIn: "bg-sky-700",
  Reddit: "bg-orange-600",
  YouTube: "bg-red-600",
  Email: "bg-emerald-500",
  "QR Code": "bg-amber-500",
  Direct: "bg-slate-500",
  Internal: "bg-slate-300 dark:bg-slate-700",
  Other: "bg-zinc-400",
};

export default function TrafficSourcesCard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/admin/traffic-sources?days=${days}`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && toast.error(formatApiError(e, "Could not load traffic sources")))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [days]);

  const sources = data?.sources || [];
  const totalPv = data?.total_page_views || 0;
  const totalSessions = data?.total_unique_sessions || 0;

  return (
    <section
      className="mb-6 rounded-2xl border border-border bg-card p-5"
      data-testid="traffic-sources-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Compass size={18} weight="duotone" className="text-primary" />
            <h2 className="font-display font-semibold text-lg tracking-tight">
              Traffic sources
            </h2>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Where your unique visitors are actually coming from. Tracks
            <code className="mx-1">document.referrer</code> + <code>utm_source</code> on the
            landing page. Direct = blank referrer (typed URL, bookmark, PWA,
            most messaging-app deeplinks).
          </p>
        </div>
        <div className="inline-flex bg-muted rounded-full p-0.5 flex-none">
          {WINDOWS.map((w) => (
            <button
              type="button"
              key={w.value}
              onClick={() => setDays(w.value)}
              data-testid={`traffic-sources-range-${w.value}`}
              className={`h-7 px-3 rounded-full text-xs font-semibold transition ${
                days === w.value
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="mt-5 h-24 bg-muted/60 rounded-lg animate-pulse" />
      ) : sources.length === 0 ? (
        <div className="mt-5 text-sm text-muted-foreground text-center py-6">
          No page_view rows yet for this window.
          <div className="text-[11px] mt-1">
            Page-view tracking was added recently — older data won&apos;t carry a source.
          </div>
        </div>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2.5">
            <SummaryStat label="Page views" value={totalPv} />
            <SummaryStat label="Unique sessions" value={totalSessions} />
            <SummaryStat label="# of channels" value={sources.length} />
            <SummaryStat
              label="Direct share"
              value={
                sources.find((s) => s.source === "Direct")
                  ? `${sources.find((s) => s.source === "Direct").share_pct}%`
                  : "0%"
              }
            />
          </div>

          <div className="mt-5 divide-y divide-border rounded-xl border border-border overflow-hidden">
            {sources.map((s) => {
              const tint = SOURCE_TINTS[s.source] || "bg-zinc-400";
              const isExpanded = expanded === s.source;
              const hasTopRefs = (s.top_referrers || []).length > 0;
              return (
                <div
                  key={s.source}
                  className="px-3 py-2.5"
                  data-testid={`traffic-source-row-${s.source.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <button
                    type="button"
                    onClick={() =>
                      hasTopRefs ? setExpanded(isExpanded ? null : s.source) : null
                    }
                    className={`w-full flex items-center gap-3 text-left ${
                      hasTopRefs ? "cursor-pointer" : "cursor-default"
                    }`}
                  >
                    <div className="w-40 sm:w-48 flex-none min-w-0">
                      <div className="text-sm font-semibold truncate">
                        {s.source}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {s.unique_sessions} sessions
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full ${tint} transition-[width] duration-500`}
                        style={{ width: `${Math.max(2, s.share_pct)}%` }}
                      />
                    </div>
                    <div className="w-20 text-right">
                      <div className="text-sm font-semibold tabular-nums">
                        {s.page_views.toLocaleString()}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {s.share_pct}%
                      </div>
                    </div>
                  </button>
                  {isExpanded && hasTopRefs && (
                    <div className="mt-2 ml-0 sm:ml-48 pl-3 border-l border-border space-y-1">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                        Top referring URLs
                      </div>
                      {s.top_referrers.map((tr, i) => (
                        <div
                          key={`${tr.referrer}-${i}`}
                          className="flex items-center gap-2 text-xs text-muted-foreground"
                        >
                          <span className="tabular-nums font-semibold text-foreground w-10 flex-none">
                            {tr.page_views}
                          </span>
                          <span className="truncate flex-1 min-w-0" title={tr.referrer}>
                            {tr.referrer}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {data?.unattributed_page_views > 0 && (
            <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">
              <strong>{data.unattributed_page_views.toLocaleString()}</strong>{" "}
              earlier page-view rows have no source attribution — they pre-date
              the referrer-capture rollout and aren&apos;t counted in the breakdown above.
            </p>
          )}
        </>
      )}
    </section>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-background/60 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="font-display font-bold text-xl tabular-nums leading-tight">
        {value}
      </div>
    </div>
  );
}
